import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Search, Shield, ShieldOff, Ban, Clock, Loader2,
  Trash2, Pin, PinOff, Users, AlertTriangle, CheckCircle, XCircle, ChevronDown, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  banned: { bg: 'bg-red-500/15 text-red-400 border-red-500/20', label: 'Banned' },
  muted: { bg: 'bg-amber-500/15 text-amber-400 border-amber-500/20', label: 'Muted' },
  timed_out: { bg: 'bg-orange-500/15 text-orange-400 border-orange-500/20', label: 'Timed Out' },
  active: { bg: 'bg-green-500/10 text-green-400 border-green-500/20', label: 'Active' },
};

const ACTION_LABELS = {
  mute: 'Muted', unmute: 'Unmuted', timeout: 'Timed Out', ban: 'Banned', unban: 'Unbanned',
  delete_message: 'Deleted Message', pin_message: 'Pinned Message', unpin_message: 'Unpinned Message',
};

function getUserStatus(u) {
  if (u.is_banned) return 'banned';
  if (u.is_muted) {
    if (u.muted_until) {
      const exp = new Date(u.muted_until);
      if (exp > new Date()) return 'timed_out';
      return 'active'; // expired timeout
    }
    return 'muted';
  }
  return 'active';
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* ─── Action Modal ─── */
const ActionModal = ({ title, onConfirm, onCancel, loading, children, confirmLabel = 'Confirm', confirmColor = 'bg-red-500 hover:bg-red-600' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="mod-action-modal">
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
      <h3 className="text-white font-bold text-sm mb-4">{title}</h3>
      {children}
      <div className="flex gap-2 mt-5">
        <button onClick={onCancel} disabled={loading}
          className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/50 text-xs font-semibold hover:bg-white/10 transition-colors"
          data-testid="mod-modal-cancel">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          className={`flex-1 py-2.5 rounded-xl text-white text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${confirmColor}`}
          data-testid="mod-modal-confirm">
          {loading && <Loader2 size={12} className="animate-spin" />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

/* ─── User Row ─── */
const UserRow = ({ user, onAction }) => {
  const status = getUserStatus(user);
  const badge = STATUS_BADGE[status];
  return (
    <div className="glass-card px-4 py-3" data-testid={`mod-user-${user.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-sm font-semibold ${user.is_anonymous ? 'text-white/40 italic' : 'text-white/80'}`}>
              {user.display_name}
            </span>
            {user.is_anonymous && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/25 font-semibold">ANON</span>}
            <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${badge.bg}`}>{badge.label}</span>
            {user.has_access && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20 font-semibold">
                {user.access_type === 'monthly' ? 'Monthly' : user.access_type === 'day_pass' ? 'Day Pass' : 'Paid'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[9px] text-white/20">
            {user.last_seen_at && <span>Seen {timeAgo(user.last_seen_at)}</span>}
            {status === 'timed_out' && user.muted_until && (
              <span className="text-orange-400/60">Expires: {new Date(user.muted_until).toLocaleTimeString()}</span>
            )}
            <span className="font-mono text-white/10">{user.id.slice(0, 8)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
          {status === 'active' && (
            <>
              <button onClick={() => onAction('mute', user)} className="p-1.5 text-amber-400/60 hover:bg-amber-400/10 rounded transition-colors" title="Mute" data-testid={`mute-btn-${user.id}`}>
                <Shield size={13} />
              </button>
              <button onClick={() => onAction('timeout', user)} className="p-1.5 text-orange-400/60 hover:bg-orange-400/10 rounded transition-colors" title="Timeout" data-testid={`timeout-btn-${user.id}`}>
                <Clock size={13} />
              </button>
              <button onClick={() => onAction('ban', user)} className="p-1.5 text-red-400/60 hover:bg-red-400/10 rounded transition-colors" title="Ban" data-testid={`ban-btn-${user.id}`}>
                <Ban size={13} />
              </button>
            </>
          )}
          {(status === 'muted' || status === 'timed_out') && (
            <>
              <button onClick={() => onAction('unmute', user)} className="px-2 py-1 text-green-400 text-[10px] font-bold hover:bg-green-400/10 rounded transition-colors" data-testid={`unmute-btn-${user.id}`}>
                UNMUTE
              </button>
              <button onClick={() => onAction('ban', user)} className="p-1.5 text-red-400/60 hover:bg-red-400/10 rounded transition-colors" title="Ban" data-testid={`ban-btn-${user.id}`}>
                <Ban size={13} />
              </button>
            </>
          )}
          {status === 'banned' && (
            <button onClick={() => onAction('unban', user)} className="px-2 py-1 text-green-400 text-[10px] font-bold hover:bg-green-400/10 rounded transition-colors" data-testid={`unban-btn-${user.id}`}>
              UNBAN
            </button>
          )}
          <button onClick={() => onAction('view_messages', user)} className="p-1.5 text-white/20 hover:text-white/50 hover:bg-white/5 rounded transition-colors" title="View Messages" data-testid={`view-msgs-btn-${user.id}`}>
            <MessageSquare size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Messages Panel ─── */
const MessagesPanel = ({ token, userId, displayName, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = userId ? `?user_id=${userId}&limit=50` : '?limit=50';
      const r = await fetch(`${API}/api/chat/admin/messages/recent${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setMessages((await r.json()).messages || []);
    } catch {}
    setLoading(false);
  }, [token, userId]);

  useEffect(() => { load(); }, [load]);

  const deleteMsg = async (msgId) => {
    const reason = prompt('Reason for deleting this message (recommended):');
    try {
      const r = await fetch(`${API}/api/chat/admin/messages/${msgId}/delete`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || null }),
      });
      if (r.ok) { toast.success('Message deleted'); load(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Failed'); }
  };

  const togglePin = async (msgId, isPinned) => {
    const action = isPinned ? 'unpin' : 'pin';
    try {
      const r = await fetch(`${API}/api/chat/admin/messages/${msgId}/${action}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) { toast.success(isPinned ? 'Unpinned' : 'Pinned'); load(); }
      else toast.error('Failed');
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="mod-messages-panel">
      <div className="flex items-center gap-2 mb-4">
        {onBack && (
          <button onClick={onBack} className="text-white/30 hover:text-white text-xs" data-testid="mod-msgs-back">&larr; Back</button>
        )}
        <h3 className="text-white/60 text-xs font-bold">
          {displayName ? `Messages by ${displayName}` : 'Recent Messages'}
        </h3>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-white/20" /></div>
      ) : messages.length === 0 ? (
        <p className="text-white/20 text-xs text-center py-8">No messages found.</p>
      ) : (
        <div className="space-y-1.5">
          {messages.map(m => (
            <div key={m.id} className={`glass-card px-3 py-2 ${m.deleted ? 'opacity-40' : ''}`} data-testid={`mod-msg-${m.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-bold text-white/40">{m.display_name}</span>
                    {m.pinned && <Pin size={9} className="text-amber-400" />}
                    {m.deleted && <span className="text-[8px] text-red-400/60 font-bold">DELETED</span>}
                    <span className="text-[9px] text-white/15">{timeAgo(m.created_at)}</span>
                  </div>
                  {m.type === 'gif' ? (
                    <span className="text-white/30 text-xs italic">GIF</span>
                  ) : (
                    <p className="text-white/50 text-xs break-words line-clamp-2">{m.content}</p>
                  )}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {!m.deleted && (
                    <>
                      <button onClick={() => togglePin(m.id, m.pinned)} className="p-1 text-white/15 hover:text-amber-400 rounded transition-colors" title={m.pinned ? 'Unpin' : 'Pin'} data-testid={`pin-msg-${m.id}`}>
                        {m.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                      </button>
                      <button onClick={() => deleteMsg(m.id)} className="p-1 text-white/15 hover:text-red-400 rounded transition-colors" title="Delete" data-testid={`del-msg-${m.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Moderation Log Panel ─── */
const LogPanel = ({ token, targetUserId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = targetUserId ? `?target_user_id=${targetUserId}&limit=100` : '?limit=100';
    fetch(`${API}/api/chat/admin/moderation-log${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setLogs(d.logs || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, targetUserId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-white/20" /></div>;
  if (logs.length === 0) return <p className="text-white/20 text-xs text-center py-8">No moderation history.</p>;

  return (
    <div className="space-y-1.5" data-testid="mod-log-list">
      {logs.map(l => {
        const actionColor = ['ban', 'delete_message'].includes(l.action) ? 'text-red-400' : ['unban', 'unmute'].includes(l.action) ? 'text-green-400' : 'text-amber-400';
        return (
          <div key={l.id} className="glass-card px-3 py-2" data-testid={`mod-log-${l.id}`}>
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className={`text-[10px] font-bold uppercase ${actionColor}`}>{ACTION_LABELS[l.action] || l.action}</span>
              <span className="text-white/30 text-[10px]">{l.target_display_name}</span>
              {l.duration_minutes && <span className="text-white/20 text-[9px]">{l.duration_minutes}min</span>}
              <span className="text-white/10 text-[9px] ml-auto">{timeAgo(l.created_at)}</span>
            </div>
            {l.reason && <p className="text-white/25 text-[10px]">Reason: {l.reason}</p>}
            <div className="text-white/10 text-[9px]">by {l.admin_display_name} ({l.admin_role})</div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Main Component ─── */
const ChatModerationAdmin = ({ token }) => {
  const [subTab, setSubTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [modalReason, setModalReason] = useState('');
  const [timeoutDuration, setTimeoutDuration] = useState(15);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (search) params.set('search', search);
      if (filter !== 'all') params.set('status', filter);
      const r = await fetch(`${API}/api/chat/admin/users?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setUsers(d.users || []);
        setTotal(d.total || 0);
      }
    } catch {}
    setLoading(false);
  }, [token, search, filter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAction = (action, user) => {
    if (action === 'view_messages') {
      setViewUser(user);
      setSubTab('messages');
      return;
    }
    if (action === 'unmute') {
      doAction(`/api/chat/admin/unmute/${user.id}`, 'POST', null, `${user.display_name} unmuted`);
      return;
    }
    if (action === 'unban') {
      doAction(`/api/chat/admin/unban/${user.id}`, 'POST', null, `${user.display_name} unbanned`);
      return;
    }
    setModal({ action, user });
    setModalReason('');
    setTimeoutDuration(15);
  };

  const doAction = async (path, method, body, successMsg) => {
    setActionLoading(true);
    try {
      const opts = { method, headers: { Authorization: `Bearer ${token}` } };
      if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
      const r = await fetch(`${API}${path}`, opts);
      if (r.ok) { toast.success(successMsg); fetchUsers(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Action failed'); }
    } catch { toast.error('Action failed'); }
    setActionLoading(false);
    setModal(null);
  };

  const confirmModal = () => {
    const { action, user } = modal;
    if (action === 'mute') {
      doAction(`/api/chat/admin/mute/${user.id}`, 'POST', { reason: modalReason || null }, `${user.display_name} muted`);
    } else if (action === 'timeout') {
      doAction(`/api/chat/admin/timeout/${user.id}`, 'POST', { duration_minutes: timeoutDuration, reason: modalReason || null }, `${user.display_name} timed out for ${timeoutDuration}min`);
    } else if (action === 'ban') {
      doAction(`/api/chat/admin/ban/${user.id}`, 'POST', { reason: modalReason || null }, `${user.display_name} banned`);
    }
  };

  const SUB_TABS = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'log', label: 'Mod Log', icon: FileText },
  ];

  return (
    <div data-testid="chat-moderation-admin">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <Shield size={18} className="text-[#10B981]" /> Chat Moderation
      </h2>
      <p className="text-white/30 text-xs mb-5">Manage chat users, messages, and moderation history.</p>

      {/* Sub tabs */}
      <div className="flex items-center gap-1.5 mb-5">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => { setSubTab(t.id); if (t.id === 'users') setViewUser(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
              subTab === t.id ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-white/5 text-white/40 border-white/10'
            }`}
            data-testid={`mod-subtab-${t.id}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {subTab === 'users' && (
        <>
          {/* Search & Filter */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 flex-1 min-w-[200px] border border-white/10">
              <Search size={13} className="text-white/25" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name..."
                className="bg-transparent text-white text-xs w-full outline-none placeholder-white/25" data-testid="mod-search-input" />
            </div>
            {['all', 'active', 'muted', 'banned'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold capitalize transition-all border ${
                  filter === f ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-white/5 text-white/40 border-white/10'
                }`}
                data-testid={`mod-filter-${f}`}>
                {f}
              </button>
            ))}
          </div>
          <p className="text-white/15 text-[10px] mb-3">{total} users</p>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[#10B981]" /></div>
          ) : users.length === 0 ? (
            <p className="text-white/20 text-sm text-center py-12">No users found.</p>
          ) : (
            <div className="space-y-1.5">
              {users.map(u => <UserRow key={u.id} user={u} onAction={handleAction} />)}
            </div>
          )}
        </>
      )}

      {/* Messages Tab */}
      {subTab === 'messages' && (
        <MessagesPanel token={token} userId={viewUser?.id} displayName={viewUser?.display_name}
          onBack={viewUser ? () => { setViewUser(null); setSubTab('users'); } : null} />
      )}

      {/* Log Tab */}
      {subTab === 'log' && <LogPanel token={token} />}

      {/* Action Modal */}
      {modal && (
        <ActionModal
          title={
            modal.action === 'mute' ? `Mute ${modal.user.display_name}?` :
            modal.action === 'timeout' ? `Timeout ${modal.user.display_name}?` :
            `Ban ${modal.user.display_name}?`
          }
          onConfirm={confirmModal}
          onCancel={() => setModal(null)}
          loading={actionLoading}
          confirmLabel={modal.action === 'ban' ? 'Ban User' : modal.action === 'mute' ? 'Mute User' : 'Apply Timeout'}
          confirmColor={modal.action === 'ban' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}
        >
          {modal.action === 'ban' && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">
              <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
              <p className="text-red-400/80 text-[10px]">This will block the user from entering Live Chat entirely. Admin-only action.</p>
            </div>
          )}
          {modal.action === 'timeout' && (
            <div className="mb-3">
              <label className="text-white/40 text-[10px] block mb-1.5">Duration</label>
              <div className="flex gap-1.5">
                {[5, 15, 30, 60].map(d => (
                  <button key={d} onClick={() => setTimeoutDuration(d)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all border ${
                      timeoutDuration === d ? 'bg-orange-500/15 text-orange-400 border-orange-500/30' : 'bg-white/5 text-white/40 border-white/10'
                    }`}
                    data-testid={`timeout-dur-${d}`}>
                    {d < 60 ? `${d}m` : '1h'}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-white/40 text-[10px] block mb-1.5">
              Reason {modal.action === 'ban' ? '(recommended)' : '(optional)'}
            </label>
            <textarea value={modalReason} onChange={e => setModalReason(e.target.value)}
              placeholder="Describe the reason for this action..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 outline-none resize-none focus:border-white/20 transition-colors"
              rows={2} data-testid="mod-reason-input" />
          </div>
        </ActionModal>
      )}
    </div>
  );
};

export default ChatModerationAdmin;
